import React, { useState } from 'react'
import {
  Box,
  Typography,
  Card,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Pagination,
  LinearProgress,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tabs,
  Tab,
} from '@mui/material'
import {
  Add,
  Edit,
  Delete,
  Collections as CollectionsIcon,
  Close,
  Star,
  Category as CategoryIcon,
  Remove,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { apiClient } from '../services/api'
import { CatalogPdfExportDialog } from '../components/catalogues/CatalogPdfExportDialog'


interface Collection {
  id: string
  name: string
  slug: string
  description?: string
  collection_type: 'manual' | 'automatic'
  is_active: boolean
  is_featured: boolean
  sort_order: number
  meta_title?: string
  meta_description?: string
}

interface Category {
  id: string
  name: string
  slug: string
  description?: string
  parent_id?: string
  is_active: boolean
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`collection-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

const CollectionsEnhancedMUI: React.FC = () => {
  const [page, setPage] = useState(1)
  const [openDialog, setOpenDialog] = useState(false)
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false)
  const [editingCollection, setEditingCollection] = useState<Collection | null>(null)
  const [selectedCollection, setSelectedCollection] = useState<Collection | null>(null)
  const [pdfExportCollection, setPdfExportCollection] = useState<Collection | null>(null)
  const [tabValue, setTabValue] = useState(0)
  const [collections, setCollections] = useState<Collection[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [collectionCategories, setCollectionCategories] = useState<Category[]>([])
  const [availableCategories, setAvailableCategories] = useState<Category[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [totalPages, setTotalPages] = useState(1)

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    collection_type: 'manual' as 'manual' | 'automatic',
    is_active: true,
    is_featured: false,
    sort_order: 0,
    meta_title: '',
    meta_description: '',
  })

  const { enqueueSnackbar } = useSnackbar()

  React.useEffect(() => {
    loadCollections()
    loadCategories()
  }, [page])

  const loadCollections = async () => {
    setLoading(true)
    try {
      const response = await apiClient.get('/admin/collections', {
        params: { page, per_page: 20 }
      })
      setCollections(response.data.items || [])
      setTotalPages(response.data.pages || 1)
    } catch (error) {
      console.error('Error loading collections:', error)
      enqueueSnackbar('Failed to load collections', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const loadCategories = async () => {
    try {
      const response = await apiClient.get('/admin/categories', {
        params: { per_page: 100 }
      })
      setCategories(response.data.items || [])
    } catch (error) {
      console.error('Error loading categories:', error)
    }
  }

  const loadCollectionCategories = async (collectionId: string) => {
    try {
      const response = await apiClient.get(`/admin/collections/${collectionId}/categories`)
      setCollectionCategories(response.data.categories || [])
      
      // Filter available categories (not already in collection)
      const categoryIds = (response.data.categories || []).map((c: Category) => c.id)
      setAvailableCategories(categories.filter(c => !categoryIds.includes(c.id)))
    } catch (error) {
      console.error('Error loading collection categories:', error)
      enqueueSnackbar('Failed to load collection categories', { variant: 'error' })
    }
  }

  const handleOpenDialog = (collection?: Collection) => {
    if (collection) {
      setEditingCollection(collection)
      setFormData({
        name: collection.name,
        description: collection.description || '',
        collection_type: collection.collection_type,
        is_active: collection.is_active,
        is_featured: collection.is_featured,
        sort_order: collection.sort_order,
        meta_title: collection.meta_title || '',
        meta_description: collection.meta_description || '',
      })
    } else {
      setEditingCollection(null)
      setFormData({
        name: '',
        description: '',
        collection_type: 'manual',
        is_active: true,
        is_featured: false,
        sort_order: 0,
        meta_title: '',
        meta_description: '',
      })
    }
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingCollection(null)
  }

  const handleSubmit = async () => {
    setSaving(true)
    try {
      if (editingCollection) {
        await apiClient.put(`/admin/collections/${editingCollection.id}`, formData)
        enqueueSnackbar('Collection updated successfully', { variant: 'success' })
      } else {
        await apiClient.post('/admin/collections', formData)
        enqueueSnackbar('Collection created successfully', { variant: 'success' })
      }
      handleCloseDialog()
      loadCollections()
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.detail || 'Failed to save collection',
        { variant: 'error' }
      )
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      try {
        await apiClient.delete(`/admin/collections/${id}`)
        enqueueSnackbar('Collection deleted successfully', { variant: 'success' })
        loadCollections()
      } catch (error: any) {
        enqueueSnackbar(
          error?.response?.data?.detail || 'Failed to delete collection',
          { variant: 'error' }
        )
      }
    }
  }

  const handleManageCategories = async (collection: Collection) => {
    setSelectedCollection(collection)
    await loadCollectionCategories(collection.id)
    setOpenCategoryDialog(true)
  }

  const handleAddCategories = async () => {
    if (!selectedCollection || selectedCategories.length === 0) return

    setSaving(true)
    try {
      await apiClient.post(
        `/admin/collections/${selectedCollection.id}/categories`,
        selectedCategories
      )
      enqueueSnackbar('Categories added successfully', { variant: 'success' })
      await loadCollectionCategories(selectedCollection.id)
      setSelectedCategories([])
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.detail || 'Failed to add categories',
        { variant: 'error' }
      )
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveCategory = async (categoryId: string) => {
    if (!selectedCollection) return

    try {
      await apiClient.delete(
        `/admin/collections/${selectedCollection.id}/categories/${categoryId}`
      )
      enqueueSnackbar('Category removed successfully', { variant: 'success' })
      await loadCollectionCategories(selectedCollection.id)
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.detail || 'Failed to remove category',
        { variant: 'error' }
      )
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Collection Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Organize categories into collections. Products are automatically included from assigned categories.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: '#f44336',
            '&:hover': { bgcolor: '#c62828' },
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 2
          }}
        >
          Add Collection
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      )}

      {!loading && (
        <Card>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#0d47a1' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Categories</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {collections.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <CollectionsIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No collections found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Get started by creating your first collection
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                        sx={{
                          bgcolor: '#f44336',
                          '&:hover': { bgcolor: '#c62828' }
                        }}
                      >
                        Create First Collection
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  collections.map((collection) => (
                    <TableRow key={collection.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography fontWeight={600}>{collection.name}</Typography>
                          {collection.is_featured && (
                            <Star sx={{ color: '#ff9800', fontSize: 16 }} />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {collection.slug}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={collection.collection_type === 'manual' ? 'Manual' : 'Automatic'}
                          color={collection.collection_type === 'manual' ? 'primary' : 'secondary'}
                          size="small"
                          sx={{ borderRadius: 4 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<CategoryIcon />}
                          onClick={() => handleManageCategories(collection)}
                          sx={{ textTransform: 'none' }}
                        >
                          Manage Categories
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={collection.is_active ? 'Active' : 'Inactive'}
                            color={collection.is_active ? 'success' : 'default'}
                            size="small"
                            sx={{ borderRadius: 4 }}
                          />
                          {collection.is_featured && (
                            <Chip
                              label="Featured"
                              color="warning"
                              size="small"
                              sx={{ borderRadius: 4 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          title="Download PDF Catalog (With/Without Price)"
                          onClick={() => setPdfExportCollection(collection)}
                          sx={{ color: '#dc2626', mr: 0.5 }}
                        >
                          <PdfIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(collection)}
                          sx={{ color: '#2196f3' }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(collection.id, collection.name)}
                          sx={{ color: '#f44336' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </Card>
      )}

      {/* Collection Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        {saving && <LinearProgress />}
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#2196f3',
          color: 'white'
        }}>
          {editingCollection ? 'Edit Collection' : 'Add Collection'}
          <IconButton onClick={handleCloseDialog} sx={{ color: 'inherit' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Collection Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Collection Type</InputLabel>
                <Select
                  value={formData.collection_type}
                  label="Collection Type"
                  onChange={(e) => setFormData({ ...formData, collection_type: e.target.value as 'manual' | 'automatic' })}
                >
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem value="automatic">Automatic</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                margin="normal"
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sort Order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  }
                  label="Active"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    />
                  }
                  label="Featured"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving || !formData.name?.trim()}
            sx={{
              bgcolor: '#f44336',
              '&:hover': { bgcolor: '#c62828' }
            }}
          >
            {saving ? 'Saving...' : (editingCollection ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog
        open={openCategoryDialog}
        onClose={() => setOpenCategoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#2196f3',
          color: 'white'
        }}>
          Manage Categories - {selectedCollection?.name}
          <IconButton onClick={() => setOpenCategoryDialog(false)} sx={{ color: 'inherit' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label={`Current Categories (${collectionCategories.length})`} />
            <Tab label="Add Categories" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {collectionCategories.length === 0 ? (
              <Alert severity="info">
                No categories in this collection yet. Switch to "Add Categories" tab to add some.
              </Alert>
            ) : (
              <List>
                {collectionCategories.map((category) => (
                  <React.Fragment key={category.id}>
                    <ListItem>
                      <ListItemText
                        primary={category.name}
                        secondary={category.description || category.slug}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveCategory(category.id)}
                          sx={{ color: '#f44336' }}
                        >
                          <Remove />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {availableCategories.length === 0 ? (
              <Alert severity="info">
                All categories are already in this collection.
              </Alert>
            ) : (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Select Categories</InputLabel>
                  <Select
                    multiple
                    value={selectedCategories}
                    onChange={(e) => setSelectedCategories(e.target.value as string[])}
                    label="Select Categories"
                    renderValue={(selected) =>
                      selected.map(id => categories.find(c => c.id === id)?.name).join(', ')
                    }
                  >
                    {availableCategories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={handleAddCategories}
                  disabled={selectedCategories.length === 0 || saving}
                  fullWidth
                  sx={{
                    bgcolor: '#f44336',
                    '&:hover': { bgcolor: '#c62828' }
                  }}
                >
                  {saving ? 'Adding...' : `Add ${selectedCategories.length} Categories`}
                </Button>
              </>
            )}
          </TabPanel>
        </DialogContent>
      </Dialog>

      {pdfExportCollection && (
        <CatalogPdfExportDialog
          open={Boolean(pdfExportCollection)}
          onClose={() => setPdfExportCollection(null)}
          targetType="collection"
          targetId={pdfExportCollection.id}
          targetName={pdfExportCollection.name}
        />
      )}
    </Box>
  )
}

export default CollectionsEnhancedMUI

